/**
 * Accessibility score utilities.
 *
 * Phase 16: Product Intelligence UX.
 *
 * Two scoring paths:
 *  - calculateAccessibilityScore   — full formula; requires all four severity counts
 *    (use on ScanDetailPage where summary endpoint data is available)
 *  - estimateAccessibilityScore    — approximation; requires only criticalIssues + totalIssues
 *    (use on Dashboard, SitesPage, SitePage where only Scan list data is available)
 */

import type { BadgeVariant } from '../components/ui/Badge';

// -------------------------------------------------------------------
// Full score (all four severities known)
// -------------------------------------------------------------------

export interface FullIssueCounts {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

/**
 * Primary formula:
 *   score = 100 − critical×25 − serious×10 − moderate×4 − minor×1
 * Clamped to [0, 100].
 */
export function calculateAccessibilityScore(counts: FullIssueCounts): number {
  const raw =
    100 -
    counts.critical * 25 -
    counts.serious * 10 -
    counts.moderate * 4 -
    counts.minor * 1;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

// -------------------------------------------------------------------
// Estimated score (only criticalIssues + totalIssues from Scan type)
// -------------------------------------------------------------------

/**
 * Approximation when full breakdown is unavailable.
 *
 * Treats non-critical issues as moderate-equivalent (×4 penalty).
 *   score = 100 − critical×25 − (total − critical)×4
 * Clamped to [0, 100].
 */
export function estimateAccessibilityScore(
  criticalIssues: number,
  totalIssues: number,
): number {
  const other = Math.max(0, totalIssues - criticalIssues);
  const raw = 100 - criticalIssues * 25 - other * 4;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

// -------------------------------------------------------------------
// Label
// -------------------------------------------------------------------

/**
 * Human-readable label for a score.
 *
 *  90–100  → Excellent
 *  75–89   → Good
 *  60–74   → Needs attention
 *   0–59   → High risk
 */
export function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Needs attention';
  return 'High risk';
}

export function getScoreLabelKey(score: number): string {
  if (score >= 90) return 'scoreLabels.excellent';
  if (score >= 75) return 'scoreLabels.good';
  if (score >= 60) return 'scoreLabels.needsAttention';
  return 'scoreLabels.highRisk';
}

// -------------------------------------------------------------------
// Color (CSS hex)
// -------------------------------------------------------------------

/**
 * Accent color for a score — use on score numerals, borders, rings.
 *
 *  90+  → #16a34a (green-600)
 *  75+  → #65a30d (lime-600)
 *  60+  → #d97706 (amber-600)
 *  <60  → #dc2626 (red-600)
 */
export function getScoreColor(score: number): string {
  if (score >= 90) return '#16a34a';
  if (score >= 75) return '#65a30d';
  if (score >= 60) return '#d97706';
  return '#dc2626';
}

// -------------------------------------------------------------------
// Badge variant
// -------------------------------------------------------------------

/**
 * Maps a score to a BadgeVariant so labels can be displayed as Badge chips.
 */
export function getScoreVariant(score: number): BadgeVariant {
  if (score >= 90) return 'success';
  if (score >= 75) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
}
